/** One-time "You've earned 7 days of full Pro" celebration, shown globally the
 * first time the backend flags a freshly earned trial (earned_trial_notice). */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { usePublicSettings } from "@/lib/siteSettings";
import { trackTrialUnlocked } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EarnedTrialModal = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const settings = usePublicSettings();
  const copy = settings["paywall_copy"] || {};
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user?.earned_trial_notice) {
      setOpen(true);
      trackTrialUnlocked(user.earned_trial_reason || "unknown");
    }
  }, [user?.earned_trial_notice, user?.earned_trial_reason]);

  if (!open) return null;

  const dismiss = async (goPlan) => {
    setOpen(false);
    try { await axios.post(`${API}/auth/trial/ack-notice`); await refresh(); } catch { /* noop */ }
    if (goPlan) navigate("/plan/today");
  };

  return (
    <div
      data-testid="earned-trial-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={() => dismiss(false)}
    >
      <div
        className="glass rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 to-transparent p-8 max-w-md w-full text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2" data-testid="earned-trial-title">
          {copy.trial_unlocked_title || "You've earned 7 days of full Pro"}
        </h2>
        <p className="text-muted-foreground mb-6">{copy.trial_unlocked_body || "Here's your plan."}</p>
        <div className="flex items-center justify-center gap-3">
          <Button data-testid="earned-trial-cta" className="rounded-full glow-primary" onClick={() => dismiss(true)}>
            {copy.trial_unlocked_cta || "See my plan"}
          </Button>
          <Button variant="ghost" className="rounded-full" onClick={() => dismiss(false)}>Later</Button>
        </div>
      </div>
    </div>
  );
};

export default EarnedTrialModal;
